<template>
  <div>

    <div class="d-flex justify-content-between mb-2">
      <b-checkbox v-model="showAssetNumber">Show Asset Numbers</b-checkbox>
    </div>

    <div class="card mb-3 no-shadow">
      <div class="card-body p-2">
        <div class="d-flex flex-wrap align-items-center">
          <template v-for="(data, propOptionId) in groupedPropOptions">
            <div class="cursor-pointer border rounded mr-2 mb-1 p-1"
                 :class="{'bg-light-green': isToggleSelected(data.value)}">

              <div @click="startEditing(data.value)">
                {{ data.name }}
                <b-badge v-if="showAssetNumber" variant="primary" title="assets">
                  {{ data.assets.length }}
                </b-badge>
              </div>

            </div>
          </template>

        </div>
      </div>
    </div>

    <!--  Asset with thumbnails / checkboxes list  -->
    <div class="card mb-3 no-shadow">
      <div class="card-body p-2">
        <div class="mb-2 d-flex align-items-start" style="gap: 5px">
          <b-button size="sm"
                    :variant="editingSelectionSelectAllButtonVariant('all')"
                    @click="editingSelectionSelectAll(true)">
            Select All
          </b-button>

          <b-button size="sm"
                    :variant="editingSelectionSelectAllButtonVariant('none')"
                    @click="editingSelectionSelectAll(false)">
            Unselect All
          </b-button>

          <div class="flex-grow-1"></div>

          <div class="d-flex gap-4">
            <b-select :options="propOptions" v-model="editingValue"/>

            <b-button variant="primary"
                      class="no-wrap"
                      size="sm"
                      :disabled="hasDeactivatedAssets || bulkActionRunning || editingSelectionIds.length === 0 || editingValue === null"
                      @click="saveCategoryOnSelection">
              <span v-if="bulkActionRunning">Please wait...</span>
              <span v-else>Apply to Selection</span>
            </b-button>
          </div>
        </div>

        <div v-if="hasDeactivatedAssets" class="font-italic text-danger text-right pb-1">{{ deactivateMessage || 'Some assets cannot be changed by this filter'}}</div>

        <div class="d-flex flex-wrap align-items-center">
          <div v-b-tooltip
               :title="deactivatedReasonsTooltip(asset)"
               class="cursor-pointer border rounded mr-2 mb-1 p-1"
               :class="{ 'border-danger': deactivatedReasons(asset) }" v-for="asset in selectedLoadedAssets">
            <b-checkbox v-model="editingSelection[asset.id]">
              <div class="img-bg img-bg-1 img-square" :style="`background-image: url('${asset.thumb_url}')`"></div>
            </b-checkbox>
          </div>
        </div>
      </div>

    </div>

  </div>
</template>

<script>

import GeneralMixins from "../../../../mixins/GeneralMixins";
import PulseLoader from 'vue-spinner/src/PulseLoader';
import TagSelect from "../../../Common/TagSelect";
import union from 'lodash/union';
import normalize from "json-api-normalize";
import Attributes from "../../../../models/Attributes.js";
import {isEqual} from "lodash";

export default {
  props: ['selectedAssets', 'idKey', 'propName', 'propOptions', 'preselectAll', 'deactivateUsages', 'deactivateStatuses', 'deactivatePropValues', 'deactivateMessage'],
  components: {PulseLoader},
  mixins: [GeneralMixins],

  data() {
    return {
      idKeyName: this.idKey || "id",
      editingValue: null,
      editingSelection: {},
      actionModal: false,
      actionType: null,
      showAssetNumber: true,
      bulkActionRunning: false,
      loaded: false,
      selectedLoadedAssets: []
    }
  },

  mounted() {
    if (this.idKeyName === "id") {
      this.loaded = true
      this.selectedLoadedAssets = this.selectedAssets
    } else {
      const assetIds = this.selectedAssets.map(el => el[this.idKeyName])

      this.$http.post("/curators/bulk/asset_loader", {asset_ids: assetIds}).then(result => {
        this.selectedLoadedAssets = normalize(result.body).get(Attributes.assetWithIncludesAttributes);
        if (this.preselectAll) {
          this.editingSelectionSelectAll(true)
        }
      }, rejectionResult => {
        this.showErrorToast(null, rejectionResult.body.errors)
      })
    }
  },

  methods: {
    toggleEditing(propOptionId) {
      this.startEditing(propOptionId)
    },

    deactivatedReasons(asset) {
      let reasons = []
      if (this.deactivateUsages && this.deactivateUsages.includes(asset.usage)) {
        reasons.push("Editorial")
      }
      if (this.deactivateStatuses && this.deactivateStatuses.includes(asset.status)) {
        reasons.push("Deactivated")
      }
      if (reasons.length === 0) {
        return undefined
      }

      return reasons
    },

    deactivatedReasonsTooltip(asset) {
      const reasons = this.deactivatedReasons(asset)
      if (!reasons) {
        return undefined
      }
      return this.deactivatedReasons(asset).join(", ")
    },

    startEditing(propOptionId) {
      this.editingSelection = Object.assign({}, ...this.selectedLoadedAssets.map(asset => {
        return {
          [asset.id]: this.assetContainsPropOptionId(asset.id, propOptionId)
        }
      }))
    },

    isToggleSelected(propOptionId) {
      const count = this.selectedLoadedAssets.filter(el => el[this.propName] === propOptionId).length
      return count > 0 && count === this.editingSelectionIds.length
    },

    propOptionName(id) {
      return this.propOptions.find(p => p.value === id).text
    },

    assetContainsPropOptionId(assetId, propOptionId) {
      return !!this.selectedLoadedAssets.find(el => el.id === assetId && el[this.propName] === propOptionId)
    },

    updateSelection(propOptionId, selectionIds) {
      if (propOptionId === null || selectionIds.length === 0) { // false is a valid propOptionId option
        return;
      }

      const attributes = {
        asset_ids: selectionIds,
        prop: this.propName,
        value: propOptionId
      }

      this.bulkActionRunning = true

      this.$http.post("/curators/bulk/asset_exclusivity", attributes).then(result => {
        let assets = result.body.assets
        let count = assets.length

        const message = result.body.message || `${this.propName} was set to ${this.propOptionName(propOptionId)} for ${count} assets`
        this.showToast(message, "Success")

        this.selectedLoadedAssets.forEach(asset => {
          let resultAsset = assets.filter(img => img.id === asset.id)[0]
          if (resultAsset) {
            asset[this.propName] = resultAsset[this.propName]
          }
        })
      }, rejectionResult => {
        this.showErrorToast(null, rejectionResult.body.errors)
      }).then(result => {
        this.bulkActionRunning = false
      });
    },

    saveCategoryOnSelection() {
      this.updateSelection(this.editingValue, this.editingSelectionIds)
    },

    editingSelectionSelectAllButtonVariant(type) {
      if (type === 'all') {
        if (this.editingSelectionAllSelected) {
          return "primary"
        } else {
          return "outline-secondary"
        }
      } else {
        if (this.editingSelectionNoneSelected) {
          return "primary"
        } else {
          return "outline-secondary"
        }
      }
    },

    editingSelectionSelectAll(value) {
      this.editingSelection = Object.assign({}, ...this.selectedLoadedAssets.map(asset => {
        return {
          [asset.id]: value
        }
      }))
    },

    allAssetPropOptionIds(assets) {
      return [...new Set(assets.map(el => el[this.propName]))]
    },

    groupPropOptions(propOptionIds, assets) {
      return propOptionIds.reduce((obj, propOptionId) => {
        obj[propOptionId] = {
          assets: assets.filter(el => el[this.propName] === propOptionId),
          name: this.propOptionName(propOptionId),
          value: propOptionId
        }
        return obj
      }, {})
    },
  },

  computed: {
    editingSelectionAllSelected() {
      if (!this.editingSelection) {
        return false
      }

      return this.editingSelectionIds.length === this.selectedLoadedAssets.length
    },

    hasDeactivatedAssets() {
      if (this.deactivatePropValues && this.deactivatePropValues.includes(this.editingValue)) {
        return this.currentAssetSelection.some(asset => this.deactivatedReasons(asset))
      } else {
        return false
      }
    },

    editingSelectionNoneSelected() {
      if (!this.editingSelection) {
        return false
      }
      return this.editingSelectionIds.length === 0
    },

    allUniquePropOptionIds() {
      return this.allAssetPropOptionIds(this.selectedLoadedAssets)
    },

    groupedPropOptions() {
      return this.groupPropOptions(this.allUniquePropOptionIds, this.selectedLoadedAssets)
    },

    currentAssetSelection() {
      const assetIds = Object.keys(this.editingSelection).filter(k => this.editingSelection[k])
      return this.selectedLoadedAssets.filter(asset => assetIds.includes(asset.id))
    },

    groupedSelectedAssetCategories() {
      const assets = this.currentAssetSelection
      const categoryIds = this.allAssetPropOptionIds(assets)
      return this.groupPropOptions(categoryIds, assets)
    },

    editingSelectionIds() {
      if (!this.editingSelection) {
        return []
      }

      return Object.keys(this.editingSelection).filter(id => this.editingSelection[id])
    },

    modalActionDisabled() {
      return this.editingSelectionIds.length === 0
    },
  }
}
</script>